import { useMemo, useRef, useState } from 'react';

import { Select } from '../../../components/ui';
import type { FieldControlProps } from '../../../components/ui/Field';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { useNguoiDungLoc } from '../hooks/useNguoiDungLoc';
import { tuyChonNgoaiDanhSach, tuyChonNguoi } from '../nghiep-vu/nhan-nguoi';

/** Chờ người dùng ngừng gõ rồi mới tìm ở server. */
const CHO_GO_MS = 300;

export interface ChonNguoiDungProps {
  tenantId: string;
  nenTang: boolean;
  /** `user_id` đang lọc; có thể là id không có trong danh sách (đã xoá, của tenant khác). */
  value: string | undefined;
  onChange: (userId: string | undefined) => void;
  /** Tên đã biết của `value` khi danh sách không có người này (lấy từ dòng nhật ký). */
  tenDaBiet?: string | null;
  /** Dùng trong form: bề rộng và dây a11y của `Field`. Bỏ trống là ô lọc của nhật ký. */
  className?: string;
  fieldProps?: FieldControlProps;
}

/**
 * Ô lọc "Người dùng": chọn theo tên và email, giá trị là `user_id` (UUID).
 *
 * Cổng tenant tải cả danh sách một lần rồi lọc tại chỗ; cổng nền tảng tìm ở
 * server và thêm nhóm "Nhân sự nền tảng". Id đang lọc mà không có trong danh
 * sách (người đã rời tổ chức, danh sách lỗi, đã tìm sang người khác) không bao giờ
 * in UUID: hiện tên đã biết, không có thì "Không rõ tên", mã để ở `title`.
 */
export function ChonNguoiDung({ tenantId, nenTang, value, onChange, tenDaBiet, className = 'w-72 max-w-full', fieldProps }: ChonNguoiDungProps) {
  const { t } = useT();
  const dichLoi = useLoi();
  const [tim, setTim] = useState('');
  const hen = useRef<ReturnType<typeof setTimeout> | null>(null);
  const q = useNguoiDungLoc(tenantId, nenTang, tim);
  // Nhãn của người vừa chọn: tìm tiếp ở server làm người đó rơi khỏi danh sách, ô vẫn phải hiện tên.
  const [daChon, setDaChon] = useState<{ id: string; nhan: string } | null>(null);
  const khongRoTen = t('nktv.nguoi.khongRoTen');

  const goTim = (chuoi: string) => {
    if (!nenTang) return;
    if (hen.current) clearTimeout(hen.current);
    hen.current = setTimeout(() => setTim(chuoi), CHO_GO_MS);
  };

  const options = useMemo(() => {
    const muc = (u: Parameters<typeof tuyChonNguoi>[0]) => tuyChonNguoi(u, khongRoTen);
    const nhom = [
      ...(q.nguoiDung.length > 0
        ? [{ label: t('nhatKy.loc.nhomNguoiDung'), title: t('nhatKy.loc.nhomNguoiDung'), options: q.nguoiDung.map(muc) }]
        : []),
      ...(q.nhanSu.length > 0
        ? [{ label: t('nhatKy.loc.nhomNhanSu'), title: t('nhatKy.loc.nhomNhanSu'), options: q.nhanSu.map(muc) }]
        : []),
    ];
    const ngoai = tuyChonNgoaiDanhSach(
      value,
      [...q.nguoiDung, ...q.nhanSu],
      daChon?.id === value ? daChon?.nhan : tenDaBiet,
      khongRoTen,
    );
    return ngoai ? [ngoai, ...nhom] : nhom;
  }, [q.nguoiDung, q.nhanSu, value, daChon, tenDaBiet, khongRoTen, t]);

  const chon = (id: string | undefined) => {
    const u = [...q.nguoiDung, ...q.nhanSu].find((x) => x.user_id === id);
    if (id && u) setDaChon({ id, nhan: tuyChonNguoi(u, khongRoTen).label });
    onChange(id ?? undefined);
  };

  return (
    <Select<string>
      {...fieldProps}
      className={className}
      allowClear
      showSearch
      aria-label={t('nhatKy.loc.nguoi')}
      placeholder={t('nhatKy.loc.nguoiGoiY')}
      value={value}
      options={options}
      loading={q.dangTai}
      // Cổng nền tảng tìm ở server: tắt lọc tại chỗ để không lọc hai lần.
      filterOption={nenTang ? false : (input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
      onSearch={goTim}
      onChange={chon}
      onClear={() => goTim('')}
      notFoundContent={
        q.loi ? (
          <span role="alert" className="text-xs text-danger-700">
            {t('nhatKy.loc.loiNguoi', { loi: dichLoi(q.loi) })}
          </span>
        ) : q.dangTai ? (
          <span className="text-xs text-content-muted">{t('nhatKy.loc.dangTaiNguoi')}</span>
        ) : (
          <span className="text-xs text-content-muted">{t('nhatKy.loc.khongCoNguoi')}</span>
        )
      }
    />
  );
}
